// ============================================================
// 給与計算のロジック
// 会社ごとの設定（payrollSettings/{companyId}）と給与マスタ（payrollProfiles/{uid}）を
// 使って、勤怠の打刻から支給額・控除額を組み立てる。
// 画面からは calcPayroll() だけを呼べばよい。
// ============================================================

/**
 * 曜日別の所定労働時間の既定値。月〜金が9:00〜18:00（休憩60分）で実働8時間。
 * work が false の曜日は所定労働日ではない。
 */
export function defaultWorkPattern() {
  const weekday = { work: true, start: "09:00", end: "18:00", breakMin: 60 };
  const off     = { work: false, start: "", end: "", breakMin: 0 };
  return [off, { ...weekday }, { ...weekday }, { ...weekday }, { ...weekday }, { ...weekday }, off];
}

/** "09:00" を分に直す */
export function toMinutes(hhmm) {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** その曜日の所定労働時間（分）。所定労働日でなければ 0。 */
export function scheduledMinutesOf(pattern, dow) {
  const d = (pattern || [])[dow];
  if (!d || !d.work) return 0;
  const s = toMinutes(d.start), e = toMinutes(d.end);
  if (s === null || e === null) return 0;
  const span = e > s ? e - s : (24 * 60 - s) + e;   // 日をまたぐ所定にも対応
  return Math.max(0, span - (Number(d.breakMin) || 0));
}

/** シフト表で使う勤務パターンの既定値 */
export function defaultShiftPatterns() {
  return [
    { key: "early", name: "早番", start: "08:00", end: "17:00", breakMin: 60, color: "#2980b9" },
    { key: "late",  name: "遅番", start: "13:00", end: "22:00", breakMin: 60, color: "#8e44ad" },
    { key: "night", name: "夜勤", start: "22:00", end: "07:00", breakMin: 60, color: "#2c3e50" },
    { key: "off",   name: "休み", start: "",      end: "",      breakMin: 0,  color: "#95a5a6" },
  ];
}

/** 会社ごとの給与設定の既定値。労働基準法の原則どおりにしてある。 */
export function defaultSettings(companyId) {
  return {
    companyId: companyId || "",

    // 締め日・支給日
    closingDay: 31,        // 31 = 末日締め
    payMonthOffset: 1,     // 0 = 当月払い、1 = 翌月払い
    payDay: 25,

    // 所定労働時間
    dailyHours: 8,           // 法定の1日上限（8時間超が法定外残業）
    weeklyHours: 40,         // 法定の1週上限（40時間超が法定外残業）
    monthlyStdHours: 173.8,  // 月平均所定労働時間（残業単価の算定に使う）
    weekStartDow: 0,         // 週の起算曜日（0=日曜）

    // 会社標準の所定労働時間（曜日別）。個人別・シフトで上書きできる。
    workPattern: defaultWorkPattern(),

    // シフト表で使う勤務パターン
    shiftPatterns: defaultShiftPatterns(),

    // 月60時間を超える時間外の割増
    overtimeRate60: 1.5,
    monthlyOvertimeThreshold: 60,

    // 休憩の自動控除（打刻が出勤・退勤しかないため）
    autoBreak: true,
    breakOver6: 45,        // 6時間超で45分
    breakOver8: 60,        // 8時間超で60分

    // 割増率
    overtimeRate: 1.25,    // 時間外
    nightExtraRate: 0.25,  // 深夜の加算分
    holidayRate: 1.35,     // 法定休日
    nightStartHour: 22,
    nightEndHour: 5,

    // 休日
    legalHolidayDow: 0,    // 法定休日の曜日（0=日曜）
    restDows: [0, 6],      // 所定休日（0=日,6=土）

    // 社会保険料（本人負担分・％）
    healthRate: 4.985,     // 健康保険（協会けんぽの全国平均を労使折半したもの）
    careRate: 0.795,       // 介護保険（40歳以上）
    pensionRate: 9.15,     // 厚生年金
    employmentRate: 0.6,   // 雇用保険（一般の事業）

    // 通勤手当の非課税限度（月額）
    commuteTaxFreeLimit: 150000,

    // 所得税
    incomeTaxMode: "auto", // auto = 自動計算、manual = 手入力
    taxTableVerified: false, // 税額表を国税庁の資料と照合済みか
    taxTableNote: "",
    // 電子計算機等を使用する場合の月額表（甲欄）。国税庁の資料で必ず確認すること。
    // over: この金額を超え、rate: 税率(%)、deduct: 控除額
    taxBrackets: [
      { over: 0,       rate: 5.105,  deduct: 0 },
      { over: 162500,  rate: 10.21,  deduct: 8296 },
      { over: 275000,  rate: 20.42,  deduct: 36374 },
      { over: 579167,  rate: 23.483, deduct: 54113 },
      { over: 750000,  rate: 33.693, deduct: 130688 },
      { over: 1500000, rate: 40.84,  deduct: 237893 },
      { over: 3333333, rate: 45.945, deduct: 408061 },
    ],
    // 扶養親族等1人あたりの控除額（月額）
    dependentDeduction: 31667,
    // 給与所得控除等に相当する基礎的な控除（月額）
    basicDeduction: 45834,
  };
}

// 給与項目の既定値。auto は自動計算の対象を指す（空なら手入力の項目）。
// key は不変。name を変えても過去の記録が壊れないようにするため。
export function defaultItems() {
  return [
    { key: "basic",       name: "基本給",   type: "支給", auto: "basic" },
    { key: "withinOt",    name: "法定内残業手当", type: "支給", auto: "withinOvertime" },
    { key: "overtime",    name: "残業手当", type: "支給", auto: "overtime" },
    { key: "overtime60",  name: "60時間超残業手当", type: "支給", auto: "overtime60" },
    { key: "night",       name: "深夜手当", type: "支給", auto: "night" },
    { key: "holiday",     name: "休日手当", type: "支給", auto: "holiday" },
    { key: "commute",     name: "通勤手当", type: "支給", auto: "commute" },
    { key: "health",      name: "健康保険", type: "控除", auto: "health" },
    { key: "care",        name: "介護保険", type: "控除", auto: "care" },
    { key: "pension",     name: "厚生年金", type: "控除", auto: "pension" },
    { key: "employment",  name: "雇用保険", type: "控除", auto: "employment" },
    { key: "incomeTax",   name: "所得税",   type: "控除", auto: "incomeTax" },
    { key: "residentTax", name: "住民税",   type: "控除", auto: "residentTax" },
  ];
}

/** 自動計算に割り当てられる項目の一覧（項目設定の選択肢） */
export const AUTO_SOURCES = [
  { value: "",            label: "手入力（自動計算しない）" },
  { value: "basic",       label: "基本給（賃金区分から算定）" },
  { value: "withinOvertime", label: "法定内残業手当（所定超〜8時間・割増なし）" },
  { value: "overtime",    label: "残業手当（法定外・1.25倍）" },
  { value: "overtime60",  label: "60時間超残業手当（1.5倍）" },
  { value: "night",       label: "深夜手当" },
  { value: "holiday",     label: "休日手当" },
  { value: "commute",     label: "通勤手当" },
  { value: "health",      label: "健康保険" },
  { value: "care",        label: "介護保険" },
  { value: "pension",     label: "厚生年金" },
  { value: "employment",  label: "雇用保険" },
  { value: "incomeTax",   label: "所得税" },
  { value: "residentTax", label: "住民税" },
];

const R = (n) => Math.round(n || 0);

/**
 * 社会保険料の端数処理。
 * 給与から控除する場合、50銭以下は切り捨て、50銭を超えるときは切り上げる。
 */
function roundInsurance(x) {
  const n = Number(x) || 0;
  const frac = n - Math.floor(n);
  return frac <= 0.5 ? Math.floor(n) : Math.ceil(n);
}

/**
 * 介護保険（第2号被保険者）の対象かどうかを生年月日から判定する。
 * 40歳の誕生日の前日が属する月から、65歳の誕生日の前日が属する月の前月まで。
 * 生年月日が未登録なら null を返し、呼び出し側で手動チェックに従う。
 */
export function careInsuranceTarget(birthDate, yearMonth) {
  if (!birthDate || !yearMonth) return null;
  const b = new Date(birthDate);
  if (isNaN(b.getTime())) return null;

  const monthIndex = (d) => d.getFullYear() * 12 + d.getMonth();

  const at40 = new Date(b.getFullYear() + 40, b.getMonth(), b.getDate());
  at40.setDate(at40.getDate() - 1);              // 誕生日の前日
  const at65 = new Date(b.getFullYear() + 65, b.getMonth(), b.getDate());
  at65.setDate(at65.getDate() - 1);

  const [y, m] = yearMonth.split("-").map(Number);
  const target = y * 12 + (m - 1);

  return target >= monthIndex(at40) && target < monthIndex(at65);
}

// ------------------------------------------------------------
// 勤怠の集計
// ------------------------------------------------------------

/** 1日の労働時間から控除する休憩時間 */
function breakFor(minutes, settings) {
  if (!settings.autoBreak) return 0;
  if (minutes > 8 * 60) return settings.breakOver8;
  if (minutes > 6 * 60) return settings.breakOver6;
  return 0;
}

/**
 * 会社カレンダーの登録から、その日の区分を返す。
 * calendar: [{date:"2027-01-01", kind:"legal"|"holiday"|"special"|"work"}]
 */
function calendarKind(dateKey, calendar) {
  if (!calendar || calendar.length === 0) return null;
  const hit = calendar.find(d => d.date === dateKey);
  return hit ? hit.kind : null;
}

const keyOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hhmm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/** 深夜（22時〜翌5時）に重なる分数 */
function nightMinutesOf(start, end, settings) {
  const ns = settings.nightStartHour, ne = settings.nightEndHour;
  let total = 0;
  const cur = new Date(start);
  while (cur < end) {
    const h = cur.getHours();
    if (h >= ns || h < ne) total++;
    cur.setTime(cur.getTime() + 60000);
  }
  return total;
}

/**
 * 出勤と退勤を時系列で組にする。日をまたぐ勤務にも対応し、
 * 組にならなかった打刻は打刻漏れとして返す。
 */
function buildShifts(punches) {
  const sorted = [...punches].sort((a, b) => a.timestamp - b.timestamp);
  const shifts = [];
  const issues = [];
  let open = null;

  sorted.forEach(p => {
    if (p.type === "出勤") {
      if (open) issues.push({ date: keyOf(open.timestamp), issue: "退勤の打刻がありません" });
      open = p;
      return;
    }
    // 退勤
    if (!open) { issues.push({ date: keyOf(p.timestamp), issue: "出勤の打刻がありません" }); return; }
    const span = (p.timestamp - open.timestamp) / 60000;
    if (span <= 0) {
      issues.push({ date: keyOf(open.timestamp), issue: "退勤が出勤より前になっています" });
    } else if (span > 24 * 60) {
      issues.push({ date: keyOf(open.timestamp), issue: "退勤の打刻がありません（24時間を超えています）" });
    } else {
      shifts.push({ start: open.timestamp, end: p.timestamp });
    }
    open = null;
  });
  if (open) issues.push({ date: keyOf(open.timestamp), issue: "退勤の打刻がありません" });

  return { shifts, issues };
}

/** 週の通し番号。週の起算曜日（既定は日曜）で区切る。 */
function weekIndexOf(dateKey, weekStartDow) {
  const d = new Date(dateKey + "T00:00:00");
  const shift = (d.getDay() - (weekStartDow || 0) + 7) % 7;
  d.setDate(d.getDate() - shift);
  return keyOf(d);
}

/**
 * 打刻から労働時間を集計する。
 * 日をまたぐ勤務は出勤日の勤務として扱う。
 * 所定労働時間は個人別のパターン → 会社標準 → 1日8時間、の順で決まる。
 *
 * 区分は4つ。
 *   plainMin        所定内（通常の賃金）
 *   withinOtMin     法定内残業（所定超〜1日8時間・週40時間の範囲。割増なし）
 *   legalOtMin      法定外残業（1日8時間超または週40時間超。1.25倍）
 *   holidayMin      法定休日労働（1.35倍）
 */
export function summarizeAttendance(punches, settings, calendar, profile, shiftByDate) {
  const pattern = (profile && profile.useCompanyPattern === false && Array.isArray(profile.workPattern))
    ? profile.workPattern
    : (Array.isArray(settings.workPattern) ? settings.workPattern : defaultWorkPattern());

  const { shifts, issues } = buildShifts(punches);

  // 出勤日ごとにまとめる（日をまたいでも出勤した日の勤務とする）
  const byDay = {};
  shifts.forEach(sh => {
    const key = keyOf(sh.start);
    (byDay[key] = byDay[key] || []).push(sh);
  });

  const legalDayMax = (settings.dailyHours || 8) * 60;
  const days = [];

  Object.keys(byDay).sort().forEach(key => {
    const list = byDay[key].sort((a, b) => a.start - b.start);
    const raw = list.reduce((a, sh) => a + Math.round((sh.end - sh.start) / 60000), 0);
    const nightRaw = list.reduce((a, sh) => a + nightMinutesOf(sh.start, sh.end, settings), 0);

    const dow = new Date(key + "T00:00:00").getDay();
    const kind = calendarKind(key, calendar);
    const isLegalHoliday = kind === "legal"
      ? true
      : (kind === "work" || kind === "holiday" || kind === "special")
        ? false
        : dow === settings.legalHolidayDow;

    // 休憩は所定パターンの値を使う。所定が無い日は労働時間から自動控除する。
    const shiftForBreak = shiftByDate ? shiftByDate[key] : null;
    const patternBreak = shiftForBreak
      ? (Number(shiftForBreak.breakMin) || 0)
      : ((pattern[dow] && pattern[dow].work) ? (Number(pattern[dow].breakMin) || 0) : null);
    const brk = patternBreak !== null ? patternBreak : breakFor(raw, settings);
    const net = Math.max(0, raw - brk);
    // 深夜時間からは休憩を按分して差し引く
    const night = raw > 0 ? Math.max(0, Math.round(nightRaw - brk * (nightRaw / raw))) : 0;

    // 所定労働時間は「シフト → 個人別・会社標準の曜日設定」の順で決まる
    const shift = shiftByDate ? shiftByDate[key] : null;
    let scheduled;
    if (shift) {
      const ss = toMinutes(shift.start), se = toMinutes(shift.end);
      if (ss === null || se === null) {
        scheduled = 0;                       // 「休み」のシフト
      } else {
        const span = se > ss ? se - ss : (24 * 60 - ss) + se;
        scheduled = Math.max(0, span - (Number(shift.breakMin) || 0));
      }
    } else if (kind === "holiday" || kind === "special") {
      scheduled = 0;
    } else {
      scheduled = scheduledMinutesOf(pattern, dow) || (kind === "work" ? legalDayMax : 0);
    }

    days.push({
      date: key, dow, isLegalHoliday, incomplete: false,
      start: hhmm(list[0].start),
      end:   hhmm(list[list.length - 1].end),
      crossesMidnight: keyOf(list[list.length - 1].end) !== key,
      breakMin: brk,
      minutes: net,
      nightMin: night,
      scheduledMin: scheduled,
      plainMin: 0, withinOtMin: 0, legalOtMin: 0, holidayMin: 0,
    });
  });

  // 1日ごとの振り分け
  days.forEach(d => {
    if (d.isLegalHoliday) { d.holidayMin = d.minutes; return; }
    const overLegal = Math.max(0, d.minutes - legalDayMax);      // 1日8時間超
    const rest = d.minutes - overLegal;
    d.legalOtMin = overLegal;
    d.plainMin = Math.min(rest, d.scheduledMin);
    d.withinOtMin = Math.max(0, rest - d.scheduledMin);
  });

  // 週40時間超を法定外残業へ振り替える（1日8時間超で既に数えた分は除く）
  const weekly = {};
  days.forEach(d => {
    if (d.isLegalHoliday) return;
    const w = weekIndexOf(d.date, settings.weekStartDow);
    (weekly[w] = weekly[w] || []).push(d);
  });
  const weekMax = (settings.weeklyHours || 40) * 60;
  Object.values(weekly).forEach(list => {
    const base = list.reduce((a, d) => a + d.plainMin + d.withinOtMin, 0);
    let excess = Math.max(0, base - weekMax);
    if (excess === 0) return;
    // 後ろの日から順に、法定内残業 → 所定内 の順で振り替える
    for (let i = list.length - 1; i >= 0 && excess > 0; i--) {
      const d = list[i];
      const fromWithin = Math.min(d.withinOtMin, excess);
      d.withinOtMin -= fromWithin; d.legalOtMin += fromWithin; excess -= fromWithin;
      if (excess <= 0) break;
      const fromPlain = Math.min(d.plainMin, excess);
      d.plainMin -= fromPlain; d.legalOtMin += fromPlain; excess -= fromPlain;
    }
  });

  const sum = (f) => days.reduce((a, d) => a + f(d), 0);
  const legalOtMin = sum(d => d.legalOtMin);
  const threshold = (settings.monthlyOvertimeThreshold || 60) * 60;
  const over60Min = Math.max(0, legalOtMin - threshold);

  // 打刻漏れの日も一覧に混ぜて、画面で分かるようにする
  issues.forEach(i => days.push({ date: i.date, minutes: 0, incomplete: true, issue: i.issue }));
  days.sort((a, b) => a.date.localeCompare(b.date));

  return {
    workDays: days.filter(d => !d.incomplete).length,
    plainMin:    sum(d => d.plainMin),
    withinOtMin: sum(d => d.withinOtMin),
    legalOtMin,
    over60Min,
    nightMin:    sum(d => d.nightMin),
    holidayMin:  sum(d => d.holidayMin),
    days,
    incompleteDays: days.filter(d => d.incomplete),
  };
}

// ------------------------------------------------------------
// 金額の計算
// ------------------------------------------------------------

/** 1時間あたりの単価 */
function hourlyRate(profile, settings) {
  if (profile.wageType === "時給") return profile.wageAmount || 0;
  if (profile.wageType === "日給") return (profile.wageAmount || 0) / (settings.dailyHours || 8);
  return (profile.wageAmount || 0) / (settings.monthlyStdHours || 173.8);  // 月給
}

/** 所得税（電子計算機等を使用する方法・甲欄相当）。乙欄は簡易に一律で扱う。 */
function incomeTax(taxableBase, profile, settings) {
  if (settings.incomeTaxMode !== "auto") return 0;
  if (taxableBase <= 0) return 0;

  if (profile.taxTable === "乙") {
    // 乙欄は本来別表。ここでは概算として一律で計算する（要確認）。
    return R(taxableBase * 0.1021);
  }

  const dependents = Number(profile.dependents || 0);
  const base = taxableBase
    - (settings.basicDeduction || 0)
    - dependents * (settings.dependentDeduction || 0);
  if (base <= 0) return 0;

  const brackets = [...(settings.taxBrackets || [])].sort((a, b) => a.over - b.over);
  let hit = brackets[0];
  brackets.forEach(b => { if (base > b.over) hit = b; });
  return Math.max(0, R(base * (hit.rate / 100) - (hit.deduct || 0)));
}

/**
 * 給与を計算する。
 * profile: payrollProfiles のデータ、summary: summarizeAttendance の戻り値
 */
export function calcPayroll({ profile, summary, settings, yearMonth }) {
  const rate = hourlyRate(profile, settings);

  // --- 支給 ---
  let basic = 0;
  if (profile.wageType === "月給") {
    basic = profile.wageAmount || 0;
  } else if (profile.wageType === "日給") {
    basic = (profile.wageAmount || 0) * summary.workDays;
  } else {
    basic = R(rate * (summary.plainMin / 60));
  }

  // みなし残業の時間分は、法定外残業から順に差し引く
  const fixedOtMin = (profile.fixedOvertimeHours || 0) * 60;
  let remain = fixedOtMin;
  const cut = (v) => { const c = Math.min(v, remain); remain -= c; return v - c; };

  const over60Min    = cut(summary.over60Min || 0);
  const legalOtMin   = cut(Math.max(0, (summary.legalOtMin || 0) - (summary.over60Min || 0)));
  const withinOtMin  = cut(summary.withinOtMin || 0);

  // 法定内残業は割増なし（所定を超えているが1日8時間・週40時間の範囲）
  const withinOtPay = R(rate * 1.0 * (withinOtMin / 60));
  const overtimePay = R(rate * settings.overtimeRate * (legalOtMin / 60));
  const over60Pay   = R(rate * (settings.overtimeRate60 || 1.5) * (over60Min / 60));
  const nightPay    = R(rate * settings.nightExtraRate * (summary.nightMin / 60));
  const holidayPay  = R(rate * settings.holidayRate * (summary.holidayMin / 60));

  const commute = profile.commuteAllowanceUnit === "日額"
    ? (profile.commuteAllowance || 0) * summary.workDays
    : (profile.commuteAllowance || 0);

  // 支給の合計は、雇用保険と所得税の算定に使うため先に出しておく
  const totalEarnings = basic + withinOtPay + overtimePay + over60Pay + nightPay + holidayPay + commute;

  // --- 控除 ---
  const std = profile.standardMonthlyPay || 0;

  // 介護保険は生年月日があれば年齢で自動判定し、無ければ登録されたチェックに従う
  const autoCare = careInsuranceTarget(profile.birthDate, yearMonth);
  const careApplies = autoCare === null ? !!profile.careInsurance : autoCare;

  const health   = profile.healthInsurance  ? roundInsurance(std * settings.healthRate / 100) : 0;
  const care     = careApplies              ? roundInsurance(std * settings.careRate / 100)   : 0;
  const pension  = profile.pensionInsurance ? roundInsurance(std * settings.pensionRate / 100) : 0;
  const employment = profile.employmentInsurance
    ? roundInsurance(totalEarnings * settings.employmentRate / 100) : 0;

  const socialTotal = health + care + pension + employment;

  // 通勤手当は非課税限度まで課税対象から外す
  const taxFreeCommute = Math.min(commute, settings.commuteTaxFreeLimit || 0);
  const taxableBase = Math.max(0, totalEarnings - taxFreeCommute - socialTotal);
  const tax = incomeTax(taxableBase, profile, settings);

  return {
    // 項目設定の auto に対応する値。画面側が項目ごとに割り当てる。
    auto: {
      basic,
      withinOvertime: withinOtPay,
      overtime: overtimePay,
      overtime60: over60Pay,
      night: nightPay, holiday: holidayPay, commute,
      health, care, pension, employment,
      incomeTax: tax, residentTax: profile.residentTax || 0,
    },
    attendance: {
      workDays:    summary.workDays,
      plainHours:  +(summary.plainMin / 60).toFixed(2),
      withinOtHours: +(withinOtMin / 60).toFixed(2),
      overtimeHours: +(legalOtMin / 60).toFixed(2),
      over60Hours:   +(over60Min / 60).toFixed(2),
      nightHours:  +(summary.nightMin / 60).toFixed(2),
      holidayHours: +(summary.holidayMin / 60).toFixed(2),
      // みなし残業を差し引く前の実績
      rawWithinOtHours: +((summary.withinOtMin || 0) / 60).toFixed(2),
      rawOvertimeHours: +((summary.legalOtMin || 0) / 60).toFixed(2),
      // 確定後に打刻を直しても金額の根拠が追えるよう、日別の内訳を残す
      days: summary.days,
      incompleteDays: summary.incompleteDays,
    },
    hourlyRate: R(rate),
  };
}

/** 対象年月（YYYY-MM）から、締め日にもとづく集計期間を求める */
export function periodOf(yearMonth, settings) {
  const [y, m] = yearMonth.split("-").map(Number);
  const closing = settings.closingDay || 31;
  if (closing >= 31) {
    return { from: new Date(y, m - 1, 1, 0, 0, 0), to: new Date(y, m, 0, 23, 59, 59) };
  }
  // 例: 20日締め → 前月21日〜当月20日
  return {
    from: new Date(y, m - 2, closing + 1, 0, 0, 0),
    to:   new Date(y, m - 1, closing, 23, 59, 59),
  };
}

export const yen = (n) => `${Number(n || 0).toLocaleString("ja-JP")}円`;
